/*
 * Site-wide estimated delivery date.
 *
 * FR carts (any flower/hash product) ship in 4 working days, all others in 2.
 * Dispatch is same day before 10am UK time on a weekday, otherwise the next
 * weekday. All time comes from a server-rendered timestamp anchored to
 * performance.now(), so the visitor's clock can't skew the cutoff.
 *
 * Renders into three targets when present:
 *  - #expected-delivery-date on the cart page (countdown before cutoff)
 *  - a theme-owned .delivery-estimate-drawer div injected near the top of the
 *    UpCart drawer body (no UpCart admin configuration involved)
 *  - #product-delivery-estimate below the product-page buy area, typed by the
 *    viewed product's own tags (data-delivery-type), not by the cart
 *
 * Cart type comes from server-side Liquid only (/cart.js JSON has no product
 * tags): .delivery-date-data spans rendered by snippets/delivery-estimate-data,
 * re-fetched via the Section Rendering API after UpCart cart changes.
 */
(() => {
  'use strict';

  const DRAWER_CLASS = 'delivery-estimate-drawer';
  const UPCART_EVENT_BASE = 'aftersell-upcart:public-events';

  // Working-day counts and the matching customer-facing ranges shown in the
  // cart-page info tooltip — keep the numbers in sync here
  const DELIVERY_RULES = {
    UK: { workingDays: 2, rangeText: '1-3 working days' },
    FR: { workingDays: 3, rangeText: '2-5 working days' },
  };

  const state = { serverMs: 0, serverSeconds: 0, perfAtRead: 0, cartType: 'UK' };

  // Write only when the value changed, so updates never touch the DOM
  // needlessly — this also makes the drawer MutationObserver self-terminating
  function setText(element, value) {
    if (element.textContent !== value) element.textContent = value;
  }

  // Adopt a carrier span only when it is strictly fresher than current state:
  // duplicate carriers and the 60s tick must never re-anchor an old timestamp
  // to a new performance.now(), which would freeze the clock.
  function adoptCarrier(el) {
    if (!el || !el.dataset) return false;
    const serverSeconds = parseInt(el.dataset.serverTime, 10);
    if (!serverSeconds || serverSeconds <= state.serverSeconds) return false;
    state.serverSeconds = serverSeconds;
    state.serverMs = serverSeconds * 1000;
    state.perfAtRead = performance.now();
    state.cartType = el.dataset.cartType || 'UK';
    return true;
  }

  function hydrateFromDom() {
    let freshest = null;
    let freshestSeconds = 0;
    document.querySelectorAll('.delivery-date-data').forEach((el) => {
      const seconds = parseInt(el.dataset.serverTime, 10) || 0;
      if (seconds > freshestSeconds) {
        freshestSeconds = seconds;
        freshest = el;
      }
    });
    if (freshest) adoptCarrier(freshest);
    return state.serverMs > 0;
  }

  function computeEstimate(deliveryType) {
    const nowMs = state.serverMs + (performance.now() - state.perfAtRead);

    const ukParts = (epochMs) => {
      const parts = {};
      new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hourCycle: 'h23',
      })
        .formatToParts(new Date(epochMs))
        .forEach((part) => (parts[part.type] = part.value));
      return parts;
    };
    const isWeekend = (parts) => parts.weekday === 'Saturday' || parts.weekday === 'Sunday';
    const DAY_MS = 24 * 60 * 60 * 1000;
    const CUTOFF_MINUTES = 8 * 60;

    const nowParts = ukParts(nowMs);
    const minutesIntoDay = parseInt(nowParts.hour, 10) * 60 + parseInt(nowParts.minute, 10);
    const beforeCutoff = !isWeekend(nowParts) && minutesIntoDay < CUTOFF_MINUTES;

    let cursor = nowMs;
    let parts = nowParts;
    if (!beforeCutoff) {
      do {
        cursor += DAY_MS;
        parts = ukParts(cursor);
      } while (isWeekend(parts));
    }
    const dispatchParts = parts;

    const rules = DELIVERY_RULES[deliveryType] || DELIVERY_RULES.UK;
    let workingDays = rules.workingDays;
    while (workingDays > 0) {
      cursor += DAY_MS;
      parts = ukParts(cursor);
      if (!isWeekend(parts)) workingDays--;
    }

    const minutesLeft = CUTOFF_MINUTES - minutesIntoDay;
    const countdown =
      minutesLeft >= 60 ? `${Math.floor(minutesLeft / 60)}h ${minutesLeft % 60}m` : `${minutesLeft}m`;

    return { beforeCutoff, countdown, dispatchParts, deliveryParts: parts, rules };
  }

  function renderCartPage(est) {
    const container = document.getElementById('expected-delivery-date');
    if (!container) return;

    const rangeSpan = document.getElementById('delivery-info-range');
    if (rangeSpan) setText(rangeSpan, est.rules.rangeText);

    const parts = est.deliveryParts;
    const dateHtml = `<strong class="green-text" style="color: #428a69">${parts.weekday} ${parts.day} ${parts.month}</strong>`;
    const html = est.beforeCutoff
      ? `Order within <strong>${est.countdown}</strong> for delivery by ${dateHtml}`
      : `Estimated delivery: ${dateHtml}`;

    container.dataset.cartType = state.cartType;
    if (container.innerHTML !== html) {
      container.innerHTML = html;
      console.log(
        `[delivery-date] cart type: ${state.cartType}, dispatch: ${est.dispatchParts.weekday}, ${est.dispatchParts.month} ${est.dispatchParts.day}, delivery: ${parts.weekday}, ${parts.month} ${parts.day}`
      );
    }
  }

  // --- UpCart drawer injection --------------------------------------------

  // UpCart sets window.upcartDocumentOrShadowRoot to document, or to its open
  // shadow root when the "render cart in shadow DOM" setting is on
  function upcartScope() {
    const scope = window.upcartDocumentOrShadowRoot;
    return scope && typeof scope.querySelectorAll === 'function' ? scope : document;
  }

  // Insert our own element near the top of the drawer body, below the
  // free-shipping rewards bar. Anchors use UpCart's unminified hook classes
  // (hard-coded in upcart-bundle.js, distinct from its hashed CSS-module
  // names); live drawer body order is: external integrations, rewards bar,
  // then .upcart-products-section (the items list).
  function ensureDrawerEl() {
    const scope = upcartScope();
    const existing = scope.querySelector(`.${DRAWER_CLASS}`);
    if (existing) return existing;

    const body = scope.querySelector('.upcart-cart-body');
    if (!body) return null;

    let before = body.querySelector(':scope > .upcart-products-section');
    if (!before) {
      const rewards = body.querySelector('.upcart-rewards');
      if (rewards) {
        // The rewards bar sits inside an anonymous wrapper; insert after the
        // wrapper that is a direct child of the body
        let wrapper = rewards;
        while (wrapper.parentElement && wrapper.parentElement !== body) wrapper = wrapper.parentElement;
        before = wrapper.nextSibling;
      } else {
        before = body.firstChild;
      }
    }

    const el = document.createElement('div');
    el.className = DRAWER_CLASS;
    // Label inherits the drawer's text colour; the date is styled inline in
    // renderDrawer since theme CSS can't reach into UpCart's shadow root
    el.style.cssText = 'margin:0;padding:4px 16px 8px;font-size:0.85em;text-align:center;';
    body.insertBefore(el, before);
    console.log('[delivery-date] inserted drawer estimate element');
    armDrawerObserver();
    return el;
  }

  function renderDrawer(est) {
    const el = ensureDrawerEl();
    if (!el) return;
    const parts = est.deliveryParts;
    // Same write-on-change innerHTML guard as renderCartPage, so the drawer
    // MutationObserver can't loop on our own writes
    const html = `Estimated delivery: <strong style="color:#428a69;font-weight:700">${parts.weekday} ${parts.day} ${parts.month}</strong>`;
    if (el.innerHTML !== html) el.innerHTML = html;
  }

  // UpCart re-renders wipe the injected element; the observer re-inserts
  // after quantity changes that emit no public event. Our own writes are
  // change-guarded, so the observer can't loop.
  let observedRoot = null;
  let observerTimerId = null;

  function armDrawerObserver() {
    const scope = upcartScope();
    const target = scope === document ? document.getElementById('upCart') : scope;
    if (!target || target === observedRoot) return;
    observedRoot = target;
    new MutationObserver(() => {
      clearTimeout(observerTimerId);
      observerTimerId = setTimeout(renderAll, 100);
    }).observe(target, { childList: true, subtree: true });
  }

  // --- Product page --------------------------------------------------------

  // Static per viewed product (typed by its own tags, not the cart), but
  // recomputed on the 60s tick so the date rolls over past the 10am cutoff
  function renderProductPage() {
    const el = document.getElementById('product-delivery-estimate');
    if (!el) return;
    const parts = computeEstimate(el.dataset.deliveryType).deliveryParts;
    const html = `Estimated delivery: <strong style="color:#428a69;font-weight:700">${parts.weekday} ${parts.day} ${parts.month}</strong>`;
    if (el.innerHTML !== html) el.innerHTML = html;
  }

  // --- Rendering + server refresh -----------------------------------------

  function renderAll() {
    if (!hydrateFromDom()) return;
    const est = computeEstimate(state.cartType);
    renderCartPage(est);
    renderDrawer(est);
    renderProductPage();
  }

  // Cart contents changed from a page whose Liquid-rendered carrier is now
  // stale (UpCart add/remove, bfcache restore): re-ask the server, since
  // product tags are not available client-side
  let refreshPromise = null;

  function refreshFromServer() {
    if (refreshPromise) return refreshPromise;
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    refreshPromise = fetch(`${cartUrl}?sections=delivery-estimate-data`)
      .then((response) => response.json())
      .then((sections) => {
        const html = sections['delivery-estimate-data'];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const carrier = doc.querySelector('.delivery-date-data');
        if (carrier && adoptCarrier(carrier)) {
          // Sync the persistent page carriers so later DOM hydrations agree
          document.querySelectorAll('.delivery-date-data').forEach((el) => {
            el.dataset.serverTime = carrier.dataset.serverTime;
            el.dataset.cartType = carrier.dataset.cartType;
          });
        }
        renderAll();
      })
      .catch((error) => console.error('[delivery-date] section refresh failed:', error))
      .finally(() => {
        refreshPromise = null;
      });
    return refreshPromise;
  }

  // Debounce so a burst of UpCart events triggers a single fetch
  let refreshTimerId = null;

  function requestRefresh() {
    clearTimeout(refreshTimerId);
    refreshTimerId = setTimeout(refreshFromServer, 150);
  }

  function init() {
    renderAll();

    // Cart page: cart.js re-renders .js-contents (fresh carrier included)
    // before publishing, so a DOM hydration is enough — no server fetch
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, renderAll);
    }

    // UpCart public events (dispatched on window by upcart-bundle.js).
    // Cart-mutating events need server truth; render-only events just re-scan.
    [
      `${UPCART_EVENT_BASE}:cart-loaded`,
      `${UPCART_EVENT_BASE}:cart-updated`,
      `${UPCART_EVENT_BASE}:added-to-cart`,
      `${UPCART_EVENT_BASE}:item-removed`,
      `${UPCART_EVENT_BASE}:upsells:added-to-cart`,
    ].forEach((name) => window.addEventListener(name, requestRefresh));
    [`${UPCART_EVENT_BASE}:cart-opened`, `${UPCART_EVENT_BASE}:upsells:rendered`].forEach((name) =>
      window.addEventListener(name, () => {
        armDrawerObserver();
        renderAll();
      })
    );

    // Back/forward-cache restores can resurrect a stale carrier
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) requestRefresh();
    });

    // Minute tick keeps the cutoff countdown and the 10am flip live
    setInterval(renderAll, 60 * 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
